import { Ref, ref } from 'vue'

interface StartStreamChunk {
  prompt: string
}

interface ProgressStreamChunk {
  message: string
}

interface FinishStreamChunk {
  citations: number[] | null
  created_at: Date
  id: number
  session_id: string
}

type StreamChunk = (StartStreamChunk | ProgressStreamChunk | FinishStreamChunk) & {
  status: 'start' | 'finish' | 'progress'
}

interface UseChatStreamParams {
  stream: ReadableStream
  onChunk: (params: { data: string }) => void
  onReady: (params: { data: FinishStreamChunk }) => void
}

interface ProcessStreamParams extends UseChatStreamParams {
  data: Ref<string>
  stopStream: Ref<boolean>
}

async function processStream({ stream, onChunk, onReady, stopStream, data }: ProcessStreamParams) {
  const reader = stream.pipeThrough(new TextDecoderStream()).getReader()

  const finishChunk = ref<FinishStreamChunk>()

  while (!stopStream.value) {
    const streamResult = await reader.read()
    if (streamResult.done || stopStream.value) break

    const chunks = streamResult.value
      .split('\n')
      .filter((line: string) => line.trim().length > 0)
      .filter((line: string) => !line.startsWith('event:'))
      .map<StreamChunk>((line: string) => JSON.parse(line.replace(/^data: /, '')))

    for (const chunk of chunks) {
      if (chunk.status === 'finish') {
        finishChunk.value = chunk as FinishStreamChunk
      }

      if (chunk.status === 'progress') {
        const progressChunk = chunk as ProgressStreamChunk
        data.value += progressChunk.message
        onChunk({ data: progressChunk.message })
      }
    }
  }

  if (stopStream.value) {
    await reader.cancel()
    stopStream.value = false
  }

  if (typeof finishChunk.value !== 'undefined') {
    onReady({ data: finishChunk.value })
  }
}

export function useChatStream(params: UseChatStreamParams) {
  const data = ref<string>('')
  const stopStream = ref<boolean>(false)

  processStream({ ...params, data, stopStream })

  const stopChatStream = () => {
    stopStream.value = true
  }

  return { data, stopChatStream }
}
